import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";

const optionButtonStyle = (active) => ({
    border: "1px solid",
    borderColor: active ? "#284eb8" : "#d7dff4",
    background: active ? "#eef3ff" : "#ffffff",
    color: active ? "#1f3f9d" : "#5f6b8a",
    borderRadius: "999px",
    padding: "10px 14px",
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.25,
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minHeight: "48px",
});

const bulletStyle = (active) => ({
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: `2px solid ${active ? "#284eb8" : "#c8d2ee"}`,
    background: active ? "#284eb8" : "#ffffff",
    boxShadow: active ? "0 0 0 4px rgba(40, 78, 184, 0.12)" : "none",
    flexShrink: 0,
});

const parseDuration = (value) => {
    const numeric = Number.parseInt(value, 10);
    return Number.isNaN(numeric) ? 999 : numeric;
};

const normalizeAgeGroup = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    if (raw === "18_24" || raw === "18-24" || raw === "18-24 years") return "18_24";
    if (raw === "25_34" || raw === "25-34" || raw === "25-34 years") return "25_34";
    if (raw === "35_plus" || raw === "35+" || raw === "35+ years") return "35_plus";
    return raw;
};

const normalizeExperienceLevel = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    if (raw === "dont_know" || raw === "i don't know driving" || raw === "i do not know driving") return "dont_know";
    if (raw === "underconfident" || raw === "i am underconfident") return "underconfident";
    if (raw === "know_drive" || raw === "i know how to drive") return "know_drive";
    if (raw === "drive_well" || raw === "i know driving well") return "drive_well";
    return raw;
};

const normalizeLicenseStatus = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (raw === "yes" || raw === "y" || raw === "true" || raw === "1") return "yes";
    if (raw === "no" || raw === "n" || raw === "false" || raw === "0") return "no";
    return raw === "any" ? "any" : raw;
};

const scoreCourse = (course, answers) => {
    let score = 0;
    const duration = parseDuration(course.duration);
    const ageGroups = Array.isArray(course.recommendation_age_groups)
        ? course.recommendation_age_groups.map(normalizeAgeGroup).filter(Boolean)
        : [];
    const experienceLevels = Array.isArray(course.recommendation_experience_levels)
        ? course.recommendation_experience_levels.map(normalizeExperienceLevel).filter(Boolean)
        : [];
    const licenseStatus = normalizeLicenseStatus(course.recommendation_license_status || "any");
    const selectedAge = normalizeAgeGroup(answers.ageGroup);
    const selectedExperience = normalizeExperienceLevel(answers.experienceLevel);
    const selectedLicense = normalizeLicenseStatus(answers.licenseStatus);

    if (ageGroups.length > 0 && !ageGroups.includes(selectedAge)) return -1;
    if (experienceLevels.length > 0 && !experienceLevels.includes(selectedExperience)) return -1;
    if (licenseStatus && licenseStatus !== "any" && licenseStatus !== selectedLicense) return -1;

    score += ageGroups.length > 0 ? 10 : 1;
    score += experienceLevels.length > 0 ? 10 : 1;
    score += licenseStatus && licenseStatus !== "any" ? 10 : 1;
    if (selectedAge === "35_plus" && duration <= 18) score += 1;
    if (selectedAge === "18_24" && duration >= 10 && duration <= 30) score += 1;

    return score;
};

const CourseRecommendationModal = ({
    show,
    onClose,
    courses = [],
    onSelectCourse,
    onViewCourse,
    onApplyPreferences,
}) => {
    const [ageGroup, setAgeGroup] = useState("");
    const [experienceLevel, setExperienceLevel] = useState("");
    const [licenseStatus, setLicenseStatus] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const allAnswered = Boolean(ageGroup && experienceLevel && licenseStatus);

    const recommendedCourses = !allAnswered || !Array.isArray(courses) || courses.length === 0
        ? []
        : [...courses]
            .map((course) => ({
                ...course,
                recommendationScore: scoreCourse(course, {
                    ageGroup,
                    experienceLevel,
                    licenseStatus,
                }),
                normalizedDuration: parseDuration(course.duration),
            }))
            .filter((course) => course.recommendationScore > 0)
            .sort((first, second) => {
                if (second.recommendationScore !== first.recommendationScore) {
                    return second.recommendationScore - first.recommendationScore;
                }
                return first.normalizedDuration - second.normalizedDuration;
            })
            .slice(0, 3);

    useEffect(() => {
        if (show) {
            setIsSubmitting(false);
            setShowResults(false);
        }
    }, [show]);

    if (!show) {
        return null;
    }

    const handleConfirm = async () => {
        if (!allAnswered || isSubmitting) {
            return;
        }

        const answers = { ageGroup, experienceLevel, licenseStatus };
        setIsSubmitting(true);

        await new Promise((resolve) => {
            window.setTimeout(resolve, 850);
        });

        if (typeof onApplyPreferences === "function") {
            await Promise.resolve(onApplyPreferences(answers));
            setIsSubmitting(false);
            onClose?.();
            return;
        }
        setIsSubmitting(false);
        setShowResults(true);
    };

    const renderOption = (label, value, selectedValue, onSelect) => (
        <button
            key={value}
            type="button"
            style={optionButtonStyle(selectedValue === value)}
            onClick={() => onSelect(value)}
        >
            <span style={bulletStyle(selectedValue === value)} />
            <span>{label}</span>
        </button>
    );

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 13000,
                background: "rgba(9, 20, 48, 0.72)",
                backdropFilter: "blur(5px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px",
            }}
        >
            <div
                style={{
                    width: "min(960px, 100%)",
                    maxHeight: "min(94vh, 860px)",
                    overflowY: "auto",
                    borderRadius: "22px",
                    background: "#ffffff",
                    boxShadow: "0 30px 70px rgba(11, 27, 63, 0.26)",
                    position: "relative",
                    padding: "18px",
                }}
            >
                {isSubmitting ? (
                    <div
                        style={{
                            minHeight: "520px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            gap: "22px",
                        }}
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close recommendation popup"
                            style={{
                                position: "absolute",
                                top: "14px",
                                right: "14px",
                                width: "40px",
                                height: "40px",
                                border: "0",
                                borderRadius: "14px",
                                background: "#284eb8",
                                color: "#ffffff",
                                fontSize: "1.5rem",
                                lineHeight: 1,
                                cursor: "pointer",
                            }}
                        >
                            x
                        </button>
                        <div
                            className="course-recommendation-loader"
                            style={{
                                width: "120px",
                                height: "120px",
                                borderRadius: "50%",
                                border: "5px solid #cfd9ff",
                                borderTopColor: "#284eb8",
                            }}
                        />
                        <h3
                            style={{
                                margin: 0,
                                color: "#1a2d83",
                                fontSize: "2rem",
                                lineHeight: 1.15,
                                fontWeight: 800,
                            }}
                        >
                            Recommending the best course for you
                        </h3>
                    </div>
                ) : null}

                {!isSubmitting ? (
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close recommendation popup"
                    style={{
                        position: "absolute",
                        top: "14px",
                        right: "14px",
                        width: "40px",
                        height: "40px",
                        border: "0",
                        borderRadius: "14px",
                        background: "#284eb8",
                        color: "#ffffff",
                        fontSize: "1.5rem",
                        lineHeight: 1,
                        cursor: "pointer",
                    }}
                >
                    x
                </button>
                ) : null}

                {!isSubmitting ? (
                <div>
                    <div
                        style={{
                            borderRadius: "24px",
                            padding: "18px",
                            background: "#ffffff",
                            border: "1px solid #e6ebf7",
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                marginBottom: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                                <span
                                    aria-hidden="true"
                                    style={{
                                        width: "60px",
                                        height: "60px",
                                        borderRadius: "50%",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "transparent",
                                        color: "#111111",
                                    }}
                                >
                                    <i className="bi bi-emoji-smile" style={{ fontSize: "2rem", lineHeight: 1 }}></i>
                                </span>
                        </div>
                        <h3
                            style={{
                                fontSize: "1.7rem",
                                lineHeight: 1.15,
                                marginBottom: "8px",
                                color: "#132347",
                                fontWeight: 800,
                            }}
                        >
                            Help Us Recommend the Right Course
                        </h3>
                        <p
                            style={{
                                marginBottom: "16px",
                                color: "#5a6888",
                                fontSize: "0.95rem",
                                lineHeight: 1.5,
                            }}
                        >
                            Answer three quick questions and we will highlight the courses
                            that match the recommendation rules configured by admin.
                        </p>

                        <div style={{ display: "grid", gap: "16px" }}>
                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    What's your Age?
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["18-24 years", "18_24"],
                                        ["25-34 years", "25_34"],
                                        ["35+ years", "35_plus"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, ageGroup, setAgeGroup)
                                    )}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    Rate your Driving Experience ?
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["I don't know driving", "dont_know"],
                                        ["I am underconfident", "underconfident"],
                                        ["I know how to drive", "know_drive"],
                                        ["I know driving well", "drive_well"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, experienceLevel, setExperienceLevel)
                                    )}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    Do you have a 4-wheeler license?
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["Yes", "yes"],
                                        ["No", "no"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, licenseStatus, setLicenseStatus)
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {!allAnswered ? (
                        <div
                            style={{
                                marginTop: "16px",
                                borderRadius: "16px",
                                border: "1px dashed #c9d4f3",
                                background: "#f8faff",
                                padding: "16px",
                                color: "#627191",
                                lineHeight: 1.5,
                            }}
                        >
                            We will recommend the best-fit courses here after you complete
                            the three preference questions.
                        </div>
                    ) : !showResults ? (
                        <div
                            style={{
                                marginTop: "18px",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                variant="primary"
                                className="rounded-pill px-5"
                                style={{ minWidth: "170px", minHeight: "48px", fontWeight: 700 }}
                                onClick={handleConfirm}
                            >
                                OK
                            </Button>
                        </div>
                    ) : recommendedCourses.length === 0 ? (
                        <div
                            style={{
                                marginTop: "16px",
                                borderRadius: "16px",
                                border: "1px dashed #c9d4f3",
                                background: "#f8faff",
                                padding: "16px",
                                color: "#627191",
                                lineHeight: 1.5,
                            }}
                        >
                            No course recommendations are available right now.
                        </div>
                    ) : (
                        <div style={{ display: "grid", gap: "10px", marginTop: "16px" }}>
                            <div
                                style={{
                                    fontWeight: 800,
                                    color: "#15264d",
                                    fontSize: "1.15rem",
                                }}
                            >
                                Recommended Courses
                            </div>
                            {recommendedCourses.map((course) => (
                                <div
                                    key={course.id}
                                    style={{
                                        borderRadius: "16px",
                                        border: "1px solid #e4e9f8",
                                        padding: "14px",
                                        background: "linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                                gap: "10px",
                                                marginBottom: "6px",
                                            }}
                                        >
                                        <h4
                                            style={{
                                                margin: 0,
                                                color: "#172a54",
                                                    fontSize: "0.98rem",
                                                    fontWeight: 700,
                                                }}
                                            >
                                            {course.title}
                                        </h4>
                                        <span
                                            style={{
                                                borderRadius: "999px",
                                                background: "#eef3ff",
                                                color: "#284eb8",
                                                    padding: "5px 9px",
                                                    fontSize: "0.76rem",
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                }}
                                        >
                                            {course.duration} Day
                                        </span>
                                    </div>
                                    <p
                                        style={{
                                            marginBottom: "10px",
                                            color: "#627191",
                                            lineHeight: 1.4,
                                            fontSize: "0.88rem",
                                        }}
                                    >
                                        {(course.description || "Structured practical and theory-based driving training.")
                                            .slice(0, 140)}
                                        {(course.description || "").length > 140 ? "..." : ""}
                                    </p>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "8px",
                                        }}
                                    >
                                        <Button
                                            variant="primary"
                                            className="rounded-pill px-4"
                                            onClick={() => {
                                                if (typeof onSelectCourse === "function") {
                                                    onSelectCourse(course.id);
                                                } else if (course.slug && typeof onViewCourse === "function") {
                                                    onViewCourse(course);
                                                }
                                                onClose?.();
                                            }}
                                        >
                                            Enroll Now
                                        </Button>
                                        {course.slug ? (
                                            <Button
                                                variant="outline-primary"
                                                className="rounded-pill px-4"
                                                onClick={() => onViewCourse?.(course)}
                                            >
                                                View Details
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                ) : null}

                <style>
                    {`
                        .course-recommendation-loader {
                            animation: courseRecommendationSpin 0.9s linear infinite;
                        }

                        @keyframes courseRecommendationSpin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }

                    `}
                </style>
            </div>
        </div>
    );
};

export default CourseRecommendationModal;
